import { useState } from 'react';
import { line, Color, Size, t, type CertificateRequestState, type DateFormat } from '@bms2/shared';
import { Button, Pill, Select } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, post, useRows } from './api';
import { whenText } from '../../shell/viewerDates';
import styles from './RecordPage.module.css';
import { useNotice } from '../../components/controls/useNotice';

/** A site's certificate requests, where the panel reads them and the window sends one (decision 0679). */
export function certificateRequestsPath(siteId: string): string {
  return `${PAGES_PATH}/sites/${siteId}/certificate-requests`;
}

/** One request as the route answers it; the token never leaves the mail. */
export interface CertificateRequestView {
  id: string;
  contactName: string;
  sentToEmail: string;
  requestedAt: string;
  expiresAt: string;
  state: CertificateRequestState;
  requestedBy: string;
}

const STATE_TONE: Readonly<Record<CertificateRequestState, 'good' | 'warning' | 'inactive'>> = { open: 'good', used: 'inactive', expired: 'warning', revoked: 'inactive' };
const STATE_KEY = { open: 'certificateRequest.state.open', used: 'certificateRequest.state.used', expired: 'certificateRequest.state.expired', revoked: 'certificateRequest.state.revoked' } as const;

export interface CertificateRequestsPanelProps {
  siteId: string;
  apiBaseUrl?: string;
  zone: string;
  dateFormat: DateFormat;
  /** Whether the viewer may write the Site; a reader sees the requests and no controls. */
  editable: boolean;
  /** The page's own reload, so an upload that landed shows on the certificate tile. */
  onChanged?: () => void;
}

/**
 * 🔒 Request a certificate, under the site's Tax exempt certificates tile
 * (decision 0679): the requests sent from this site with where each stands,
 * Revoke link on an open one, and the button that sends a contact a single-use
 * upload link.
 */
export function CertificateRequestsPanel({ siteId, apiBaseUrl = '', zone, dateFormat, editable, onChanged }: CertificateRequestsPanelProps) {
  const requests = useRows<CertificateRequestView>(certificateRequestsPath(siteId), apiBaseUrl);
  const [sending, setSending] = useState(false);
  const [messageBanner, setMessage] = useNotice();
  const rows = requests.state.kind === 'ready' ? requests.state.data : [];
  const revoke = async (id: string) => {
    setMessage(null);
    const answer = await post(`${certificateRequestsPath(siteId)}/${id}/revoke`, {}, apiBaseUrl);
    if (!answer.ok) return setMessage({ tone: 'error', text: answer.message });
    setMessage({ tone: 'good', text: t('certificateRequest.revoked') });
    requests.reload?.();
  };
  return (
    <div className={styles.verify} data-certificate-requests={siteId}>
      <span className={styles.verifyTitle}>{t('certificateRequest.title')}</span>
      {messageBanner}
      {rows.length === 0 ? <p className={styles.hint}>{t('certificateRequest.none')}</p> : null}
      {rows.map((row) => (
        <div key={row.id} className={styles.actions} data-certificate-request={row.id} data-certificate-request-state={row.state}>
          <Pill text={t(STATE_KEY[row.state])} foreground={`var(--status-${STATE_TONE[row.state]}-text)`} background={`var(--status-${STATE_TONE[row.state]}-bg)`} size={Size.Small} />
          <span className={styles.hint}>{line(row.contactName, '·', row.sentToEmail, '·', t('certificateRequest.sent'), whenText(row.requestedAt, zone, dateFormat), '·', t('certificateRequest.until'), whenText(row.expiresAt, zone, dateFormat))}</span>
          {editable && row.state === 'open' ? <Button size={Size.Small} color={Color.Inactive} text={[t('certificateRequest.revoke')]} actions={[() => void revoke(row.id)]} /> : null}
        </div>
      ))}
      {editable ? (
        <div className={styles.actions}>
          <Button size={Size.Small} color={Color.Navy} text={[t('certificateRequest.request')]} actions={[() => setSending(true)]} />
        </div>
      ) : null}
      {sending ? (
        <CertificateRequestModal
          siteId={siteId}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setSending(false)}
          onSent={(email) => {
            setSending(false);
            setMessage({ tone: 'good', text: line(t('certificateRequest.sentTo'), email) });
            requests.reload?.();
            onChanged?.();
          }}
        />
      ) : null}
    </div>
  );
}

/** The window that sends the link: one contact of the account with a work email (decision 0679). */
export function CertificateRequestModal({ siteId, apiBaseUrl = '', onCancel, onSent }: { siteId: string; apiBaseUrl?: string; onCancel: () => void; onSent: (email: string) => void }) {
  const contacts = useRows<{ personId: string; name: string; email: string }>(`${certificateRequestsPath(siteId)}/contacts`, apiBaseUrl);
  const [personId, setPersonId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const options = contacts.state.kind === 'ready' ? contacts.state.data : [];
  const send = async () => {
    setError(null);
    if (!personId) return setError(t('certificateRequest.pickContact'));
    setBusy(true);
    const answer = await post<{ sentToEmail: string }>(certificateRequestsPath(siteId), { contactPersonId: personId }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSent(answer.data.sentToEmail);
  };
  return (
    <Modal open title={t('certificateRequest.request')} onCancel={onCancel} dataModal="certificate-request" actions={[{ kind: 'primary', label: t('certificateRequest.send'), onAct: () => void send(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel }]}>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <p className={styles.hint}>{t('certificateRequest.hint')}</p>
      <Select size={Size.Large} label={t('certificateRequest.contact')} value={personId} onChange={setPersonId} placeholder={t('certificateRequest.pickContact')} options={options.map((row) => ({ value: row.personId, label: line(row.name, '·', row.email) }))} dataAttribute={{ 'data-certificate-request-contact': 'true' }} />
      {contacts.state.kind === 'ready' && options.length === 0 ? <p className={styles.hint} data-certificate-request-contacts="none">{t('certificateRequest.noContacts')}</p> : null}
    </Modal>
  );
}
